import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Switch } from '@/components/forms/switch'
import { Textarea } from '@/components/forms/textarea'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'

import type { OidcClientScopePayload } from './api'

const emptyValues: OidcClientScopePayload = {
  description: '',
  display_name: '',
  enabled: true,
  include_in_token_scope: true,
  name: '',
}

function suggestedScopeName(displayName: string) {
  return displayName
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9._:-]+/g, '.')
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '')
    .slice(0, 64)
}

export function OidcClientScopeFormDrawer({
  isSaving,
  onOpenChange,
  onSave,
  open,
}: {
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSave: (payload: OidcClientScopePayload) => void
  open: boolean
}) {
  const { t } = useTranslation('adminIdp')
  const [values, setValues] = useState(emptyValues)
  const [nameEdited, setNameEdited] = useState(false)

  return (
    <Drawer
      footer={
        <div className="flex justify-end gap-3">
          <Button onClick={() => onOpenChange(false)} variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={!values.name.trim() || !values.display_name.trim()}
            isLoading={isSaving}
            onClick={() => onSave(values)}
          >
            {t('actions.create')}
          </Button>
        </div>
      }
      onOpenChange={onOpenChange}
      open={open}
      title={t('clientScopes.create')}
    >
      <div className="grid gap-6">
        <div className="rounded-xl border border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] px-4 py-3 text-sm leading-6 text-[var(--color-text-muted)]">
          {t('clientScopes.createHint')}
        </div>
        <FormField
          hint={t('clientScopes.fields.displayNameHint')}
          htmlFor="client-scope-display-name"
          label={t('clientScopes.fields.displayName')}
          required
        >
          <Input
            autoFocus
            id="client-scope-display-name"
            onChange={(event) => {
              const display_name = event.target.value
              setValues({
                ...values,
                display_name,
                name: nameEdited ? values.name : suggestedScopeName(display_name),
              })
            }}
            value={values.display_name}
          />
        </FormField>
        <FormField
          hint={t('clientScopes.fields.nameHint')}
          htmlFor="client-scope-name"
          label={t('clientScopes.fields.name')}
          required
        >
          <Input
            className="font-mono"
            id="client-scope-name"
            onChange={(event) => {
              setNameEdited(true)
              setValues({ ...values, name: event.target.value })
            }}
            placeholder={t('clientScopes.placeholders.name')}
            value={values.name}
          />
        </FormField>
        <FormField
          hint={t('clientScopes.fields.descriptionHint')}
          htmlFor="client-scope-description"
          label={t('clientScopes.fields.description')}
        >
          <Textarea
            id="client-scope-description"
            onChange={(event) => setValues({ ...values, description: event.target.value })}
            placeholder={t('clientScopes.placeholders.description')}
            rows={3}
            value={values.description}
          />
        </FormField>
        <label className="flex items-start justify-between gap-5 rounded-xl border border-[var(--color-panel-border)] px-4 py-3">
          <span>
            <span className="block text-sm font-medium">
              {t('clientScopes.fields.includeInTokenScope')}
            </span>
            <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
              {t('clientScopes.fields.includeInTokenScopeHint')}
            </span>
          </span>
          <Switch
            checked={values.include_in_token_scope}
            onCheckedChange={(include_in_token_scope) =>
              setValues({ ...values, include_in_token_scope })
            }
          />
        </label>
      </div>
    </Drawer>
  )
}
